/**
 * Предпросмотр и подготовка проведения запроса клиента
 * (перенос prepareRequestPosting_ / buildPreviewPayload_ из Requests.gs 3.13.2).
 */
import { requestKey } from '../engine/engine.js';
import { isBlank, normalizeArticle, numOrNull, round2, samePrice, text } from '../engine/values.js';
import { formatDay, formatMoney, formatPrice, isIsoDay } from '../format.js';
import { REQUEST_COLUMNS } from './columns.js';
import { computeRequestChecks, isRequestRowActive } from './validate.js';
const fail = (message) => ({ ok: false, message });
function summarize(items) {
    let positions = 0;
    let qty = 0;
    let amount = 0;
    for (const it of items) {
        if (it.qty <= 0)
            continue;
        positions++;
        qty += it.qty;
        if (it.price !== null)
            amount += it.qty * it.price;
    }
    return { positions, qty, amount: round2(amount) };
}
export function prepareRequestPosting(header, rows, eng) {
    const check = computeRequestChecks(header, rows);
    if (check.errors > 0)
        return fail(`Строк с ошибками: ${check.errors}. Исправьте их (см. столбец «Проверка») и повторите.`);
    const reqNo = text(header.reqNo);
    if (!reqNo)
        return fail('Укажите № запроса.');
    const date = text(header.date) || eng.today;
    if (!isIsoDay(date))
        return fail('Дата указана некорректно.');
    const deadlineInput = text(header.deadline);
    if (deadlineInput && !isIsoDay(deadlineInput))
        return fail('Срок поставки указан некорректно.');
    const comment = text(header.comment);
    const current = eng.byKey[requestKey(reqNo)] ?? null;
    const items = rows.filter(isRequestRowActive).map((r) => ({
        art: normalizeArticle(r.art),
        name: text(r.name),
        qty: numOrNull(r.qty) ?? 0,
        price: isBlank(r.price) ? null : numOrNull(r.price),
        note: text(r.note)
    }));
    const ops = [];
    let deadlineChange = null;
    let unchanged = 0;
    let before = { positions: 0, qty: 0, amount: 0 };
    let after;
    if (header.mode === 'new') {
        if (current)
            return fail(`Запрос № ${current.no} уже существует. Для правок выберите режим «Изменение запроса».`);
        if (!items.length)
            return fail('Нет строк для проведения.');
        for (const it of items) {
            ops.push({ type: 'create', art: it.art, name: it.name, qtyOld: 0, qtyNew: it.qty, priceOld: null, priceNew: it.price, note: it.note });
        }
        after = summarize(items);
    }
    else {
        if (!current)
            return fail(`Запрос № ${reqNo} не найден. Выберите номер из списка.`);
        const byArt = {};
        for (const it of current.items)
            byArt[normalizeArticle(it.art)] = it;
        const resulting = {};
        for (const it of current.items)
            resulting[normalizeArticle(it.art)] = { qty: it.qty, price: it.price };
        const seen = {};
        for (const it of items) {
            seen[it.art] = true;
            const cur = byArt[it.art];
            if (!cur || cur.qty === 0) {
                if (it.qty === 0) {
                    unchanged++;
                    continue;
                }
                const price = it.price !== null ? it.price : (cur?.price ?? null);
                ops.push({
                    type: 'add',
                    art: it.art,
                    name: it.name || cur?.name || '',
                    qtyOld: 0,
                    qtyNew: it.qty,
                    priceOld: cur?.price ?? null,
                    priceNew: price,
                    note: it.note
                });
                resulting[it.art] = { qty: it.qty, price };
                continue;
            }
            const newName = it.name || cur.name;
            const newPrice = it.price !== null ? it.price : cur.price;
            const noteChanged = it.note !== '' && it.note !== cur.note;
            const changed = it.qty !== cur.qty || !samePrice(newPrice, cur.price) || newName !== cur.name || noteChanged;
            if (!changed) {
                unchanged++;
                continue;
            }
            ops.push({
                type: it.qty === 0 ? 'remove' : 'change',
                art: it.art,
                name: newName,
                nameOld: cur.name,
                qtyOld: cur.qty,
                qtyNew: it.qty,
                priceOld: cur.price,
                priceNew: newPrice,
                note: it.note || cur.note
            });
            resulting[it.art] = { qty: it.qty, price: newPrice };
        }
        for (const cur of current.items) {
            const a = normalizeArticle(cur.art);
            if (cur.qty > 0 && !seen[a]) {
                ops.push({
                    type: 'remove',
                    art: a,
                    name: cur.name,
                    qtyOld: cur.qty,
                    qtyNew: 0,
                    priceOld: cur.price,
                    priceNew: cur.price,
                    note: 'Нет в актуальной таблице клиента'
                });
                resulting[a] = { qty: 0, price: cur.price };
            }
        }
        if (deadlineInput && deadlineInput !== current.deadline) {
            deadlineChange = { from: current.deadline, to: deadlineInput };
        }
        if (!ops.length && !deadlineChange) {
            return fail(`Изменений нет: таблица совпадает с текущим состоянием запроса № ${current.no}.`);
        }
        before = summarize(current.items);
        after = summarize(Object.values(resulting));
    }
    const deadline = deadlineInput || current?.deadline || '';
    const isNew = header.mode === 'new';
    const groupDefs = [
        { type: 'create', title: 'Новые позиции', tone: 'add' },
        { type: 'add', title: 'Добавлено', tone: 'add' },
        { type: 'change', title: 'Изменено', tone: 'change' },
        { type: 'remove', title: 'Удалено (количество станет 0)', tone: 'change' }
    ];
    const groups = groupDefs
        .map((g) => ({
        title: g.title,
        tone: g.tone,
        columns: ['OEM', 'Наименование', 'Кол-во, шт', 'Цена продажи, CNY', 'Примечание'],
        numeric: [2, 3],
        rows: ops
            .filter((o) => o.type === g.type)
            .map((o) => [
            o.art,
            o.nameOld && o.nameOld !== o.name ? `${o.nameOld} → ${o.name}` : o.name,
            o.type === 'create' ? String(o.qtyNew) : `${o.qtyOld} → ${o.qtyNew}`,
            o.type === 'create' || o.type === 'add' || samePrice(o.priceOld, o.priceNew)
                ? formatPrice(o.priceNew)
                : `${formatPrice(o.priceOld)} → ${formatPrice(o.priceNew)}`,
            o.note
        ])
    }))
        .filter((g) => g.rows.length > 0);
    const meta = [
        ['Режим', isNew ? 'Новый запрос' : 'Изменение запроса'],
        ['№ запроса', reqNo],
        ['Дата', formatDay(date)],
        ['Срок поставки', deadlineChange ? `${formatDay(deadlineChange.from)} → ${formatDay(deadlineChange.to)}` : formatDay(deadline || null)]
    ];
    if (comment)
        meta.push(['Комментарий', comment]);
    const cards = isNew
        ? [
            { label: 'Позиций', value: String(after.positions) },
            { label: 'Штук', value: String(after.qty) },
            { label: 'Сумма для клиента', value: formatMoney(after.amount) }
        ]
        : [
            { label: 'Позиций', value: `${before.positions} → ${after.positions}` },
            { label: 'Штук', value: `${before.qty} → ${after.qty}` },
            { label: 'Сумма для клиента', value: `${formatMoney(before.amount)} → ${formatMoney(after.amount)}` }
        ];
    const warnings = [];
    if (unchanged)
        warnings.push(`Без изменений: ${unchanged} поз.`);
    const removed = ops.filter((o) => o.type === 'remove');
    if (removed.length)
        warnings.push(`Снимается позиций: ${removed.length}. Уже закупленное по ним останется как излишек.`);
    return {
        ok: true,
        view: {
            title: `${isNew ? 'Новый запрос' : 'Изменение запроса'} № ${reqNo}`,
            meta,
            notice: isNew
                ? 'После проведения запрос попадёт в работу к отделу закупки.'
                : 'Отдел закупки увидит новые количества при следующем выборе запроса.',
            warnings,
            cards,
            groups
        },
        op: { kind: 'request', mode: header.mode, reqNo, date, deadline, comment, items: ops, deadlineChange }
    };
}
/** Строки формы из текущего состава запроса (режим «Изменение запроса»). */
export function requestTemplateRows(items) {
    return items
        .filter((it) => it.qty > 0)
        .map((it) => ({
        art: it.art,
        name: it.name,
        qty: String(it.qty),
        price: it.price === null ? '' : String(it.price),
        note: it.note
    }));
}
export const REQUEST_INPUT_KEYS = REQUEST_COLUMNS.map((c) => c.key);
//# sourceMappingURL=preview.js.map